import React, { useEffect, useState } from "react";

import { Button, Variant } from "@packages/ds";
import { useIntl } from "react-intl";

import Loading from "ds/PageLoading";
import { Tooltip } from "ds/Tooltip";
import useDecodedParams from "Hooks/useDecodedParams";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import { getOrganizationId } from "Libs/utils";
import {
  organizationEstimateSelector,
  organizationLoadingSelector
} from "Reducers/organization";
import { addonsSelector, getAddons } from "Reducers/organization/addons";
import { useTrialInfo } from "Reducers/organization/hooks/useTrialnfo";
import { useLoadOrder } from "Reducers/organization/order/hooks/useLoadOrder";
import { useLoadOrders } from "Reducers/organization/order/hooks/useLoadOrders";
import { getOrganizationOrderAsOfNow } from "Reducers/organization/order/thunks/getOrganizationOrderAsOfNow.thunk";
import { organizationProfileSelector } from "Reducers/organization/profile";
import BillingWrapper from "src/organization/pages/billing/components/BillingWrapper/BillingWrapper";
import useOrganizationFromURL from "src/organization/pages/settings/pages/members/hooks/useOrganizationFromURL";
import { useAppDispatch, useAppSelector } from "Store/hooks";

import AddonCta from "../components/Overview/AddonCta";
import Aside from "../components/Overview/Aside";
import * as O from "../components/Overview/BillingOverview.styles";
import Commitments from "../components/Overview/Commitments";

import {
  UserManagementAddonModal,
  OrgSupportAddonModal
} from "./components/AddonModals";
import OrganizationBreakdown from "./components/OrganizationBreakdown";
import * as S from "./OrganizationBilling.style";

const OrganizationBilling = () => {
  const intl = useIntl();
  const dispatch = useAppDispatch();

  const { organizationId } = useDecodedParams<{
    organizationId: string;
  }>();

  const organizationEstimate = useSelectorWithUrlParams(
    organizationEstimateSelector
  );
  const organizationIsLoading = useAppSelector(organizationLoadingSelector);

  const { recurring, loading: loadingOrders } = useLoadOrders(organizationId);

  const organization = useOrganizationFromURL();

  const recurringOrders = Object.values(recurring || {})[0];

  const { order, loading: loadingOrder } = useLoadOrder({
    organizationId,
    orderId: recurringOrders?.id,
    includeDetails: true
  });

  const orgId = useAppSelector(state =>
    getOrganizationId(state, organizationId)
  );

  const asOfOrder = useAppSelector(
    state => state.organizationOrder.asOf[order?.id ?? ""]
  );

  const addons = useSelectorWithUrlParams(addonsSelector);

  useEffect(() => {
    if (organization && !addons) {
      dispatch(getAddons({ organizationId: organization.name }));
    }
  }, [addons, dispatch, organization]);

  useEffect(() => {
    if (orgId && order) {
      dispatch(
        getOrganizationOrderAsOfNow({
          organizationId: orgId,
          orderId: order.id
        })
      );
    }
  }, [dispatch, order, orgId, organizationId]);

  const [showUserManagementModal, setShowUserManagementModal] = useState(false);
  const [showOrgSupportModal, setShowOrgSupportModal] = useState(false);

  const userManagementModalDirection =
    typeof addons?.current.user_management?.standard === "number"
      ? "downgrading"
      : "upgrading";

  const organizationProfile = useSelectorWithUrlParams(
    organizationProfileSelector
  );

  const { shouldShowFreeDuringTrialTag, isTrial } = useTrialInfo();

  const mtcDaysRemaining =
    Object.values(addons?.current.support_level ?? {})[0] ?? -1;

  const canUpgradeOrgSupport =
    (addons?.upgrades_available?.support_level?.length ?? 0) > 0;
  const canDowngradeOrgSupport = mtcDaysRemaining === 0;
  const showDowngradeOrgSupport =
    Object.keys(addons?.current.support_level ?? {})[0] !== "standard";

  const orgSupportButtons: React.ReactNode[] = [];

  if (canUpgradeOrgSupport) {
    orgSupportButtons.push(
      <Button
        onClick={() => {
          setShowOrgSupportModal(true);
        }}
      >
        {intl.formatMessage({
          id: `organization.billing.upgrading`
        })}
      </Button>
    );
  }

  if (showDowngradeOrgSupport) {
    const downgradeButton = (
      <Button
        disabled={!canDowngradeOrgSupport}
        onClick={() => {
          setShowOrgSupportModal(true);
        }}
        variant={canUpgradeOrgSupport ? Variant.secondary : Variant.outline}
      >
        {intl.formatMessage({
          id: `organization.billing.downgrading`
        })}
      </Button>
    );
    orgSupportButtons.push(
      canDowngradeOrgSupport ? (
        downgradeButton
      ) : (
        <Tooltip
          align="start"
          tooltip={intl.formatMessage(
            {
              id: "organization.billing.mtc_downgrade_note"
            },
            { value: mtcDaysRemaining }
          )}
        >
          {downgradeButton}
        </Tooltip>
      )
    );
  }

  const Addons = () => (
    <O.Stack>
      <O.BillingTitle tag="h2">
        {intl.formatMessage({
          id: `organization.billing.organization_addons`
        })}
      </O.BillingTitle>

      <AddonCta
        title={intl.formatMessage({
          id: "organization.billing.user_management.title"
        })}
        body={intl.formatMessage({
          id: "organization.billing.user_management.body"
        })}
        tagVariant="rose"
        tag={
          shouldShowFreeDuringTrialTag
            ? intl.formatMessage({
                id: "free_during_trial"
              })
            : undefined
        }
        buttons={
          <Button
            onClick={() => {
              setShowUserManagementModal(true);
            }}
          >
            {userManagementModalDirection
              ? intl.formatMessage({
                  id: `organization.billing.${userManagementModalDirection}`
                })
              : ""}
          </Button>
        }
      />

      <AddonCta
        title={intl.formatMessage({
          id: "organization.billing.modal.org_support"
        })}
        body={intl.formatMessage({
          id: "organization.billing.modal.org_support.body"
        })}
        buttons={orgSupportButtons}
      />
    </O.Stack>
  );

  if (loadingOrders || organizationIsLoading || loadingOrder) {
    return <Loading />;
  }
  return (
    <BillingWrapper>
      <S.PageWrapper>
        <Aside
          page="organization"
          currentSpend={asOfOrder?.total_formatted}
          totalCurrentMonth={organizationEstimate?.current.total.formatted}
          totalNextMonth={organizationEstimate?.future.total.formatted}
          isTrial={isTrial}
        >
          <Commitments organizationId={organizationId} />
          <Addons />
        </Aside>
        <OrganizationBreakdown
          order={order}
          asOfOrder={asOfOrder}
          estimateCurrent={organizationEstimate?.current}
          estimateFuture={organizationEstimate?.future}
          isTrial={isTrial}
          isEnterprise={organizationProfile?.invoiced}
        />
        <UserManagementAddonModal
          isOpen={showUserManagementModal}
          setIsOpen={setShowUserManagementModal}
        />
        <OrgSupportAddonModal
          isOpen={showOrgSupportModal}
          setIsOpen={setShowOrgSupportModal}
        />
      </S.PageWrapper>
    </BillingWrapper>
  );
};

export default OrganizationBilling;
